import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CustomerCompaniesCell } from "src/core/components/folder/stakeholders/CustomerCompaniesCell";
import { Card, CardContent } from "src/core/components/ui/card";
import { EReputationStatus } from "src/core/constants/eReputation";
import { IRiskOption } from "src/core/constants/options";
import { CustomRiskLevel, RISK_LEVEL_CLASSES } from "src/core/constants/risk";
import { formatIsoToDate } from "src/core/utils/date";

type StakeholderCardDisplayData = {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  birthName?: string | null;
  birthDate?: string | null;
  citizenshipCountryCode?: string | null;
  residenceCountryCode?: string | null;
  birthCountryCode?: string | null;
  pepStatus?: EReputationStatus | null;
  sanctionStatus?: EReputationStatus | null;
};

type StakeholderCardProps = {
  data: StakeholderCardDisplayData;
  countryWithRiskOptions: IRiskOption[];
  customerCompanyNames?: string[];
  customerCompaniesLoading?: boolean;
  withBottomBorder?: boolean;
  footer?: ReactNode;
};

const formatValue = (value?: string | null) => {
  if (!value) return "-";
  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : "-";
};

/*
 * Composant de présentation générique pour afficher une fiche stakeholder.
 * Il centralise l'affichage des informations communes entre le stakeholder courant
 * et les candidats doublons potentiels.
 */
export const StakeholderCard = ({
  data,
  countryWithRiskOptions,
  customerCompanyNames = [],
  customerCompaniesLoading = false,
  withBottomBorder = false,
  footer,
}: StakeholderCardProps) => {
  const { t } = useTranslation();

  const citizenshipCountry = useMemo(
    () => countryWithRiskOptions.find((country) => country.value === data.citizenshipCountryCode),
    [countryWithRiskOptions, data.citizenshipCountryCode]
  );

  const residenceCountry = useMemo(
    () => countryWithRiskOptions.find((country) => country.value === data.residenceCountryCode),
    [countryWithRiskOptions, data.residenceCountryCode]
  );

  const birthCountry = useMemo(
    () => countryWithRiskOptions.find((country) => country.value === data.birthCountryCode),
    [countryWithRiskOptions, data.birthCountryCode]
  );

  return (
    <Card className="w-full h-[330px] bg-gray-xxlight border-foreground/10 text-sm">
      <CardContent className="px-20 py-10 flex flex-col gap-y-10">
        <div className="flex justify-between items-center gap-x-10 h-30">
          <span className="font-medium">
            {[formatValue(data.firstName), formatValue(data.lastName)].join(" ")}
          </span>

          <div className="flex flex-row gap-x-5 font-light">
            {data.sanctionStatus === EReputationStatus.CONFIRMED && (
              <div className="flex rounded-lg border border-border text-sm text-center text-white bg-danger px-2 py-1">
                {t("stakeholders.datatable.analyse.sanction")}
              </div>
            )}

            {data.pepStatus === EReputationStatus.CONFIRMED && (
              <div className="flex rounded-lg border border-border text-sm text-center text-white bg-alert px-2 py-1">
                {t("stakeholders.datatable.analyse.pep")}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.customer")}
          </span>
          <CustomerCompaniesCell
            companyNames={customerCompanyNames}
            isLoading={customerCompaniesLoading}
          />
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.civility")}
          </span>
          <span>{formatValue(data.title)}</span>
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.firstName")}
          </span>
          <span>{formatValue(data.firstName)}</span>
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.lastName")}
          </span>
          <span>{formatValue(data.lastName)}</span>
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.birthName")}
          </span>
          <span>{formatValue(data.birthName)}</span>
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.birthDate")}
          </span>
          <span>{formatIsoToDate(data.birthDate ?? "")}</span>
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.citizenshipCountryCode")}
          </span>
          <span>{citizenshipCountry?.label ?? "-"}</span>
        </div>

        <div className="grid grid-cols-2 font-light">
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.residenceCountryCode")}
          </span>
          <div className="flex items-center gap-x-5">
            {residenceCountry ? (
              <>
                <div
                  className={`w-[16px] h-[16px] rounded-full ${
                    RISK_LEVEL_CLASSES[residenceCountry.risk ?? CustomRiskLevel.UNDEFINED]
                  }`}
                />
                <span>{residenceCountry.label}</span>
              </>
            ) : (
              <span>-</span>
            )}
          </div>
        </div>

        <div
          className={[
            "grid grid-cols-2 font-light",
            withBottomBorder ? "border-b border-foreground/10 pb-10 -mx-6 px-6" : "pb-10",
          ].join(" ")}
        >
          <span className="text-muted-foreground">
            {t("stakeholders.duplicateCheck.modal.birthCountryCode")}
          </span>
          <span>{birthCountry?.label ?? "-"}</span>
        </div>

        {footer ? <div>{footer}</div> : null}
      </CardContent>
    </Card>
  );
};
